import type { CSSProperties } from 'react';
import { readableOn } from './contrast';
import { fontStack } from './fonts';
import { DEFAULT_THEME } from './presets';
import type { Theme } from './presets';

const STORAGE_KEY = 'rd-theme';

function rgba(hex: string, opacityPercent: number): string {
  const channel = (start: number) => parseInt(hex.slice(start, start + 2), 16);
  return `rgba(${channel(1)}, ${channel(3)}, ${channel(5)}, ${opacityPercent / 100})`;
}

/** Which background a theme points at: the scope matters because the planner has its own upload. */
export function backgroundUrl(theme: Pick<Theme, 'background' | 'backgroundVersion'> & { scope?: Theme['scope'] }): string | null {
  if (theme.background === null) return null;
  if (theme.background === 'upload') {
    const scope = theme.scope === 'planner' ? '&scope=planner' : '';
    return `/api/files/background?v=${theme.backgroundVersion ?? ''}${scope}`;
  }
  return `/backgrounds/${theme.background.slice('preset:'.length)}.svg`;
}

export function logoUrl(theme: Pick<Theme, 'hasLogo' | 'logoVersion'>): string | null {
  return theme.hasLogo ? `/api/files/logo?v=${theme.logoVersion ?? ''}` : null;
}

/** A theme as CSS variables: on <html> for the website, on the timetable sheet for the weekly planner. */
export function themeVariables(theme: Theme): Record<string, string> {
  const image = backgroundUrl(theme);
  return {
    '--text': theme.textColor,
    '--accent': theme.accentColor,
    '--on-accent': readableOn(theme.accentColor),
    '--panel': rgba(theme.foregroundColor, theme.foregroundOpacity),
    '--panel-solid': theme.foregroundColor,
    '--font-heading': fontStack(theme.fontHeading),
    '--font-body': fontStack(theme.fontBody),
    '--bg-image': image ? `url("${image}")` : 'none',
  };
}

/** The same variables as a React style object (for an element that carries its own theme). */
export function themeStyle(theme: Theme): CSSProperties {
  return themeVariables(theme) as CSSProperties;
}

/** Turns the saved website theme into CSS variables on <html> (live preview and normal rendering use the same path). */
export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  for (const [name, value] of Object.entries(themeVariables(theme))) root.style.setProperty(name, value);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
  } catch {
    // Private mode or blocked storage: the theme still applies, only the no-flash copy is lost.
  }
}

/** Applies the last known theme before React renders, so a reload does not flash the default look. */
export function applyStoredTheme(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) applyTheme({ ...DEFAULT_THEME, ...(JSON.parse(raw) as Partial<Theme>) });
  } catch {
    // Corrupt or unavailable storage: keep the defaults from index.css.
  }
}
